import { ValidationType } from "~/api";

// define regexes in static context
const phoneRegEx = new RegExp(
  /^(\(|\+)?(\d{3})(\)|\s|-){0,2}(\d{3})(\)|\s|-)?(\d{4})$/,
);

const internationalPhoneRegEx = new RegExp(/^\+([0-9]+(-[0-9]+)*)?$/);

const phoneIncrementalRegEx = new RegExp(
  /^(\(|\+)?(\d{3})(\)|\s|-){0,2}(\d{0,3})?(\)|\s|-)?(\d{0,4})?$/,
);
export const emailRegEx = new RegExp(
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
);

const luhnCheck = (val: string) => {
  // from https://gist.github.com/DiegoSalazar/4075533
  if (/[^0-9-\s]+/.test(val)) return false;

  // The Luhn Algorithm. It's so pretty.
  let nCheck = 0,
    cDigit,
    nDigit,
    bEven = false;
  const rawValue = val.replace(/\D/g, "");
  for (let n = rawValue.length - 1; n >= 0; n--) {
    cDigit = rawValue.charAt(n);
    nDigit = parseInt(cDigit, 10);

    if (bEven) {
      if ((nDigit *= 2) > 9) nDigit -= 9;
    }

    nCheck += nDigit;
    bEven = !bEven;
  }
  return nCheck % 10 === 0;
};

interface ValidationDefinition {
  formatter?: (val: string) => string;
  parser?: (val: string) => string;
  validator?: (val: string) => boolean;
}
type ValidationDict = {
  [key in ValidationType]: ValidationDefinition;
};

export const validationDict: ValidationDict = {
  [ValidationType.EMAIL]: {
    parser: (val) => val.replace(/\s+/g, ""),
    validator: (val) => emailRegEx.test(val),
    formatter: (val) => val,
  },
  [ValidationType.URL]: {
    formatter: (val) => val.toLowerCase(),
    parser: (val) => val.replace(/\s+/g, ""),
  },
  [ValidationType.SIN]: {
    formatter: (val) => {
      const value = val.replace(/\D/g, "").slice(0, 9);
      return value.replace(/(\d{3})(?=\d)/g, "$1 ");
    },
    parser: (val) => val.slice(0, 11), // 9 digits + 2 whitespaces
    validator: (val) => luhnCheck(val),
  },
  [ValidationType.PHONE]: {
    formatter: (val) => {
      if (val.startsWith("+")) {
        const cleanInternationalInput = val // Only digits & non-trailing/non-leading hyphens
          .replace(/[^0-9-]/g, "")
          .replace(/-{2,}/g, "-")
          .replace(/^-+/g, "");
        return `+${cleanInternationalInput}`;
      }
      const value = val.replace(/\D/g, "").slice(0, 13);
      if (!phoneIncrementalRegEx.test(value)) {
        return value;
      }
      const matches = value.match(phoneIncrementalRegEx);

      if (!matches || !matches[2] || !matches[4]) {
        return value;
      }

      if (!matches[6]) {
        return value.replace(phoneIncrementalRegEx, "($2) $4");
      }

      return value.replace(phoneIncrementalRegEx, "($2) $4-$6");
    },
    parser: (val) => {
      if (val.startsWith("+")) {
        const cleanInternationalInput = val // Only digits & non-trailing/non-leading hyphens
          .replace(/[^0-9-]/g, "")
          .replace(/-{2,}/g, "-")
          .replace(/^-+/g, "");
        return `+${cleanInternationalInput}`;
      }
      return val.slice(0, 14); // 10 digits + 2 parentheses + 1 space + 1 dash
    },
    validator: (val) => {
      if (val.startsWith("+")) {
        return internationalPhoneRegEx.test(val);
      }
      return phoneRegEx.test(val);
    },
  },
};
